//#region IMPORT
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setStartDate, setEndDate, setYear, setMonth, setSearchText, setDepList, setDepCd } from '../../../../Reducer/SearchReducer';
import dayjs from 'dayjs';
import ACS_BASIC from './ACS_BASIC';
import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import { mapParamsBySchema } from '../../../../util';
import { GON_BASIC_Schema } from '../../../BasicInfo/Gongjong/GON_BASIC/GON_BASIC_Schema';
//#endregion

function ACS_BASICF() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);
  const [value, setValue] = useState(0);
  const executed = useRef(false);

  // 부서는 안전신문고(탭1)에서만 사용 → requireDep: false, autoFetchDep: true (탭1 전환 시 부서 목록 사용)
  const {
    handleCoChange,
    handleCustChange,
    handleDepChange,
    validateSelection: validateSelectionBase,
  } = useCompanyCustDep({
    requireCompany: true,
    requireCust: true,
    autoFetchDep: true,
  });

  const validateSelection = () => {
    if (!validateSelectionBase()) return false;
    return true;
  };

  /** 안전신문고 탭 선택 시 현재 사업장에 맞는 부서 목록 조회 (훅 수정 없이 화면에서만 호출) */
  const fetchDepForCust = useCallback(
    async (custCd) => {
      if (!custCd) return;
      try {
        const encryptedParams = mapParamsBySchema({
          CustCd: (_, context) => Commons.EncryptAES(custCd || '') || '',
          SearchText: (_, context) => Commons.EncryptAES('') || '',
        });
        const result = await apiRequest(
          'post',
          Global.urls.Utility.getDepLast,
          encryptedParams
        );
        const data = result?.data;
        let depList = [];
        if (data?.length > 0 && data[0]?.type !== 'Error') {
          depList = data.map((item) => ({
            name: item.depNmLast,
            value: item.depCd,
          }));
        }
        dispatch(setDepList(depList));
        dispatch(setDepCd(''));
      } catch (e) {
        console.error(e);
        dispatch(setDepList([]));
        dispatch(setDepCd(''));
      }
    },
    [dispatch]
  );

  /** 아차사고(탭1) 전용 검색조건: 공정 목록/선택값 (페이지 로컬 상태, Redux 미사용) */
  const [gongjongList, setGongjongList] = useState([]);
  const [gongjongCd, setGongjongCd] = useState('');

  /** 부서 선택 시 해당 부서의 공정 목록 조회. 부서 '전체'일 때는 모든 부서의 공정을 조회해 합침 (GetGonBasicR1) */
  const fetchGongjongForDep = useCallback(
    async (depCd) => {
      try {
        if (depCd) {
          const params = mapParamsBySchema(GON_BASIC_Schema.mapper.search2, {
            depCd,
          });
          const res = await apiRequest(
            'post',
            Global.urls.Gongjong.getGonBasicR1,
            params
          );
          const raw = res?.Data ?? res?.data ?? res;
          const list = Array.isArray(raw) ? raw : [];
          const nextList = list.map((r) => ({
            value: r.GongjongCd ?? r.gongjongCd ?? '',
            name: r.GongjongNm ?? r.gongjongNm ?? '',
          }));
          setGongjongList([{ value: '', name: '전체' }, ...nextList]);
        } else {
          const depList = searchInfo?.depList ?? [];
          if (depList.length === 0) {
            setGongjongList([{ value: '', name: '전체' }]);
            setGongjongCd('');
            return;
          }
          const results = await Promise.all(
            depList.map((dep) => {
              const depCdVal = dep.value ?? dep.depCd ?? '';
              if (!depCdVal) return Promise.resolve([]);
              const params = mapParamsBySchema(GON_BASIC_Schema.mapper.search2, {
                depCd: depCdVal,
              });
              return apiRequest(
                'post',
                Global.urls.Gongjong.getGonBasicR1,
                params
              ).then((res) => {
                const raw = res?.Data ?? res?.data ?? res;
                return Array.isArray(raw) ? raw : [];
              });
            })
          );
          const merged = results.flat();
          const seen = new Set();
          const nextList = merged
            .filter((r) => {
              const cd = r.GongjongCd ?? r.gongjongCd ?? '';
              if (seen.has(cd)) return false;
              seen.add(cd);
              return true;
            })
            .map((r) => ({
              value: r.GongjongCd ?? r.gongjongCd ?? '',
              name: r.GongjongNm ?? r.gongjongNm ?? '',
            }));
          setGongjongList([{ value: '', name: '전체' }, ...nextList]);
        }
        setGongjongCd('');
      } catch (e) {
        console.error(e);
        setGongjongList([]);
        setGongjongCd('');
      }
    },
    [searchInfo?.depList]
  );

  const handleGongjongChange = useCallback((value) => {
    setGongjongCd(value ?? '');
  }, []);

  const [filterClick1, setFilterClick1] = useState(false);
  const [filterClick2, setFilterClick2] = useState(false);
  const [filterClick3, setFilterClick3] = useState(false);
  const [searchClick1, setSearchClick1] = useState(false);
  const [searchClick2, setSearchClick2] = useState(false);
  const [searchClick3, setSearchClick3] = useState(false);
  const [saveClick1, setSaveClick1] = useState(false);
  const [saveClick2, setSaveClick2] = useState(false);
  const [saveClick3, setSaveClick3] = useState(false);
  const [deleteClick1, setDeleteClick1] = useState(false);
  const [deleteClick2, setDeleteClick2] = useState(false);
  const [deleteClick3, setDeleteClick3] = useState(false);
  const [printClick1, setPrintClick1] = useState(false);
  const [printClick2, setPrintClick2] = useState(false);
  const [printClick3, setPrintClick3] = useState(false);

  const startDate = searchInfo.startDate;
  const endDate = searchInfo.endDate;
  const searchYear = searchInfo.year || dayjs().format('YYYY');

  const handleYearChange = (date) => {
    const y = date ? dayjs(date).format('YYYY') : dayjs().format('YYYY');
    dispatch(setYear(y));
  };

  const handleYearMonthChange = (date) => {
    const d = date ? dayjs(date) : dayjs();
    dispatch(setYear(d.format('YYYY')));
    dispatch(setMonth(d.format('MM')));
  };

  const handleStartDateChange = (newStart) => {
    const newStartDate = dayjs(newStart).format('YYYY-MM-DD');
    let updatedEnd = searchInfo.endDate;
    if (!dayjs(newStartDate).isBefore(dayjs(updatedEnd))) {
      updatedEnd = newStartDate;
      dispatch(setEndDate(updatedEnd));
    }
    dispatch(setStartDate(newStartDate));
  };

  const handleEndDateChange = (newEnd) => {
    const newEndDate = dayjs(newEnd).format('YYYY-MM-DD');
    let updatedStart = searchInfo.startDate;
    if (dayjs(newEndDate).isBefore(dayjs(updatedStart))) {
      updatedStart = newEndDate;
      dispatch(setStartDate(updatedStart));
    }
    dispatch(setEndDate(newEndDate));
  };

  const Init = async () => {
    setSearchClick1(true);
  };

  useEffect(() => {
    if (!executed.current && searchInfo.custCd) {
      Init();
      executed.current = true;
    }
  }, [searchInfo.custCd]);

  // 안전신문고(탭1) 선택 시 조회조건의 사업장에 맞는 부서 목록 조회
  useEffect(() => {
    if (value === 1 && searchInfo.custCd) {
      fetchDepForCust(searchInfo.custCd);
    }
  }, [value, searchInfo.custCd, fetchDepForCust]);

  // 안전신문고(탭1)에서 부서 선택 시 해당 부서의 공정 목록 조회 (부서 '전체'면 모든 부서 공정 조회)
  useEffect(() => {
    if (value === 1) {
      fetchGongjongForDep(searchInfo.depCd ?? '');
    } else if (value !== 1) {
      setGongjongList([]);
      setGongjongCd('');
    }
  }, [value, searchInfo.depCd, fetchGongjongForDep]);

  // 부서·공정이 바뀔 때마다 재조회 (탭1일 때만, 부서가 선택된 경우에만 — 탭 전환 직후 부서 미선택 시 검색 트리거하지 않음)
  useEffect(() => {
    if (value === 1 && searchInfo?.depCd) {
      setSearchClick2(true);
    }
  }, [value, gongjongCd, searchInfo?.depCd]);

  const HandleChange = (event, newValue) => {
    setValue(newValue);
  };

  // 탭2(아차사고) 저장 시 02F가 반드시 최신 state로 re-render 된 뒤 Save2()가 실행되도록 동기 flush
  const setSaveClick2Sync = (v) => flushSync(() => setSaveClick2(v));

  return (
    <div className="wrap">
      <ACS_BASIC
        value={value}
        HandleChange={HandleChange}
        userInfo={userInfo}
        searchInfo={searchInfo}
        dispatch={dispatch}
        handleCoChange={handleCoChange}
        handleCustChange={handleCustChange}
        validateSelection={validateSelection}
        startDate={startDate}
        endDate={endDate}
        searchYear={searchYear}
        handleYearChange={handleYearChange}
        handleStartDateChange={handleStartDateChange}
        handleEndDateChange={handleEndDateChange}
        handleYearMonthChange={handleYearMonthChange}
        handleDepChange={(e) => handleDepChange(e?.target?.value ?? e)}
        gongjongList={gongjongList}
        gongjongCd={gongjongCd}
        handleGongjongChange={handleGongjongChange}
        setSearchText={(text) => dispatch(setSearchText(text))}
        showSnackbar={showSnackbar}
        filterClick1={filterClick1}
        setFilterClick1={setFilterClick1}
        filterClick2={filterClick2}
        setFilterClick2={setFilterClick2}
        filterClick3={filterClick3}
        setFilterClick3={setFilterClick3}
        searchClick1={searchClick1}
        setSearchClick1={setSearchClick1}
        searchClick2={searchClick2}
        setSearchClick2={setSearchClick2}
        searchClick3={searchClick3}
        setSearchClick3={setSearchClick3}
        saveClick1={saveClick1}
        setSaveClick1={setSaveClick1}
        saveClick2={saveClick2}
        setSaveClick2={setSaveClick2Sync}
        saveClick3={saveClick3}
        setSaveClick3={setSaveClick3}
        deleteClick1={deleteClick1}
        setDeleteClick1={setDeleteClick1}
        deleteClick2={deleteClick2}
        setDeleteClick2={setDeleteClick2}
        deleteClick3={deleteClick3}
        setDeleteClick3={setDeleteClick3}
        printClick1={printClick1}
        setPrintClick1={setPrintClick1}
        printClick2={printClick2}
        setPrintClick2={setPrintClick2}
        printClick3={printClick3}
        setPrintClick3={setPrintClick3}
      />
    </div>
  );
}

export default ACS_BASICF;
